import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Grid,
  Grow,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  IconButton,
  Tooltip,
  Popover,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import {
  ChildCare,
  AutoAwesome,
  Language,
  Male,
  Female,
  WhatsApp,
  Facebook,
  Logout,
  Bookmark,
  ExpandMore,
  Search,
} from "@mui/icons-material";
import { Badge } from "@mui/material";
import NameCard from "./NameCard";
import SkeletonCard from "./SkeletonCard";
import FavoritesDrawer from "./FavoritesDrawer";
import DiscoveryPanel from "./DiscoveryPanel";
import { clearToken, clearUserToken, getAuthHeaders } from "./authService";
import { useAuth } from "./AuthContext";
import { useNameGeneration } from "./useNameGeneration";
import LoginDialog from "./LoginDialog";
import "./App.css";

// Theme order for Indonesian users: Islamic and Javanese first (largest audience segments)
const THEMES_ID = [
  { value: 'general',   key: 'themeGeneral' },
  { value: 'islamic',   key: 'themeIslamic' },
  { value: 'modern',    key: 'themeModern' },
  { value: 'christian', key: 'themeChristian' },
  { value: 'javanese',  key: 'themeJavanese' },
  { value: 'arabic',    key: 'themeArabic' },
  { value: 'sundanese', key: 'themeSundanese' },
  { value: 'batak',     key: 'themeBatak' },
  { value: 'sanskrit',  key: 'themeSanskrit' },
];

const THEMES_EN = [
  { value: 'general',   key: 'themeGeneral' },
  { value: 'islamic',   key: 'themeIslamic' },
  { value: 'modern',    key: 'themeModern' },
  { value: 'christian', key: 'themeChristian' },
  { value: 'javanese',  key: 'themeJavanese' },
  { value: 'arabic',    key: 'themeArabic' },
  { value: 'sundanese', key: 'themeSundanese' },
  { value: 'batak',     key: 'themeBatak' },
  { value: 'sanskrit',  key: 'themeSanskrit' },
];

const VISIBLE_THEMES = 3;

function App() {
  const { t, i18n } = useTranslation();
  const { user, logout, favorites, favoriteNames, atFavoritesLimit, addFavorite, removeFavorite, loadFavorites, favoritesError, clearFavoritesError } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const bookmarkRef = useRef(null);
  const [onboardingPopoverOpen, setOnboardingPopoverOpen] = useState(false);
  const [firstSaveSnackbar, setFirstSaveSnackbar] = useState(false);
  const [input, setInput] = useState("");
  const [gender, setGender] = useState("boy");
  const [theme, setTheme] = useState("general");
  const [formOpen, setFormOpen] = useState(false);
  const [themeExpanded, setThemeExpanded] = useState(false);
  const locale = i18n.language === "id" ? "id" : "global";

  const themes = i18n.language === "id" ? THEMES_ID : THEMES_EN;
  const visibleThemes = themeExpanded ? themes : themes.slice(0, VISIBLE_THEMES);
  const hiddenCount = themes.length - VISIBLE_THEMES;

  const handleLogout = () => {
    logout();
    clearUserToken();
    clearToken();
  };

  const triggerOnboarding = () => {
    if (localStorage.getItem("favoritesOnboarded")) return;
    localStorage.setItem("favoritesOnboarded", "1");
    setFirstSaveSnackbar(true);
    setOnboardingPopoverOpen(true);
    setTimeout(() => setOnboardingPopoverOpen(false), 4000);
  };

  useEffect(() => {
    if (user) {
      loadFavorites();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (!gaId) return;

    const script = document.createElement("script");
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    script.async = true;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", gaId);
  }, []);

  const { names: results, loading, error: rateLimitError, generateNames, clearError } = useNameGeneration();
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [feedbackMap, setFeedbackMap] = useState({});

  const toggleLanguage = () => {
    const nextLang = i18n.language === "en" ? "id" : "en";
    i18n.changeLanguage(nextLang);
    localStorage.setItem("lang", nextLang);
  };

  const handleGenerate = () => generateNames(input, gender, locale, theme, null);
  const handleDiscoverySurprise = () => generateNames("", gender, locale, "general", null);
  const handleDiscoveryVibe = (vibeKey) => generateNames("", gender, locale, theme, vibeKey);
  const handleDiscoveryOrigin = (themeKey) => {
    setTheme(themeKey);
    generateNames("", gender, locale, themeKey, null);
  };

  const handleFeedback = async (name, isPositive) => {
    if (!user) { setLoginOpen(true); return; }
    const direction = isPositive ? 'up' : 'down';
    if (feedbackMap[name] === direction) return;
    setFeedbackMap(prev => ({ ...prev, [name]: direction }));
    try {
      const headers = await getAuthHeaders();
      await fetch('/api/Feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ Name: name, IsPositive: isPositive, Theme: theme, Gender: gender, Locale: locale }),
      });
    } catch {
      // silent fail — feedback is non-critical
    }
  };

  const handleCopy = (name) => {
    navigator.clipboard.writeText(name).catch(() => {});
    setCopySnackbar(true);
  };

  const handleShareAllWhatsApp = () => {
    const namesList = results.map(r => `• *${r.name}* — ${r.meaning}`).join('\n');
    const text = encodeURIComponent(
      `Rekomendasi nama bayi dari CumaloApp:\n\n${namesList}\n\nBuat nama bayi Anda sendiri di:\nhttps://cumalochannel.com/nama-bayi/`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleShareFacebook = () => {
    const url = encodeURIComponent('https://app.cumalochannel.com');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'noopener,noreferrer');
  };

  const hasResults = results.length > 0;

  useEffect(() => {
    if (hasResults) setFormOpen(true);
  }, [hasResults]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(150deg, #EEF2FF 0%, #ffffff 50%, #FEF3C7 100%)",
        py: 4,
      }}
    >
      <Container maxWidth="md">

        {/* Header */}
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mb: 2 }}>
            <Chip
              icon={<Language />}
              label={i18n.language === "en" ? "ID" : "EN"}
              onClick={toggleLanguage}
              size="small"
              variant="outlined"
              color="primary"
            />
            {user ? (
              <>
                <span ref={bookmarkRef}>
                  <Tooltip title={t("favoritesDrawerButton")}>
                    <IconButton size="small" onClick={() => setDrawerOpen(true)} color="primary">
                      <Badge badgeContent={favorites.length || null} color="error" max={99}>
                        <Bookmark fontSize="small" />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </span>
                <Tooltip title={user.name || user.email || ""}>
                  <Avatar
                    src={user.picture}
                    alt={user.name}
                    sx={{ width: 30, height: 30, cursor: "default" }}
                  />
                </Tooltip>
                <Tooltip title={t("logoutButton")}>
                  <IconButton size="small" onClick={handleLogout} color="primary">
                    <Logout fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Chip
                label={t("loginButton")}
                onClick={() => setLoginOpen(true)}
                size="small"
                color="primary"
              />
            )}
          </Box>
          <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
          <ChildCare
            className="bounce-icon"
            sx={{ fontSize: 56, color: "primary.main", mb: 1 }}
          />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
            <Typography variant="h3" color="primary">
              {t("appTitle")}
            </Typography>
            <Chip label="Beta" color="warning" size="small" sx={{ fontWeight: 700 }} />
          </Box>
          <Typography variant="subtitle1" color="text.secondary">
            {t("subtitle")}
          </Typography>
        </Box>

        {/* Global gender selector — applies to all generation paths */}
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={gender}
            exclusive
            onChange={(_, val) => { if (val !== null) setGender(val); }}
            sx={{
              width: "100%",
              "& .MuiToggleButton-root": {
                flex: 1,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
              },
            }}
          >
            <ToggleButton value="boy">
              <Male sx={{ mr: 0.5 }} /> {t("genderBoy")}
            </ToggleButton>
            <ToggleButton value="girl">
              <Female sx={{ mr: 0.5 }} /> {t("genderGirl")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Discovery Panel — hero when no results and not loading */}
        {!hasResults && !loading && (
          <>
            <DiscoveryPanel
              compact={false}
              onSurprise={handleDiscoverySurprise}
              onVibe={handleDiscoveryVibe}
              onOrigin={handleDiscoveryOrigin}
              loading={loading}
            />

            {/* "atau" divider */}
            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                {t("orDivider")}
              </Typography>
            </Divider>
          </>
        )}

        {/* Custom form — collapsed by default */}
        <Accordion
          expanded={formOpen}
          onChange={() => setFormOpen(!formOpen)}
          elevation={0}
          disableGutters
          sx={{
            mb: 4,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px !important",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Search fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {t("customFormTrigger")}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              label={t("customFormInputLabel")}
              placeholder={t("customFormInputPlaceholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !loading) handleGenerate();
              }}
              sx={{ mb: 2 }}
            />

            {/* Theme selector — 3 visible + "+N more" chip */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <ToggleButtonGroup
                value={theme}
                exclusive
                onChange={(_, val) => { if (val !== null) setTheme(val); }}
                size="small"
                sx={{
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 0.5,
                  "& .MuiToggleButton-root": {
                    borderRadius: 20,
                    px: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                {visibleThemes.map(({ value, key }) => (
                  <ToggleButton key={value} value={value}>{t(key)}</ToggleButton>
                ))}
              </ToggleButtonGroup>
              {!themeExpanded && (
                <Chip
                  label={`+${hiddenCount} ${t("themeShowMore")}`}
                  onClick={() => setThemeExpanded(true)}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 0.5, mt: 0.5, borderRadius: 20, fontWeight: 600, cursor: "pointer" }}
                />
              )}
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AutoAwesome />
                )
              }
              onClick={handleGenerate}
              disabled={loading}
            >
              {loading ? t("generatingButton") : t("generateButton")}
            </Button>
          </AccordionDetails>
        </Accordion>

        {/* Rate limit error */}
        {rateLimitError && (
          <Alert severity="warning" onClose={clearError} sx={{ mb: 3 }}
            action={
              rateLimitError === "rateLimitErrorAnon" && !user ? (
                <Button color="inherit" size="small" onClick={() => { clearError(); setLoginOpen(true); }}>
                  {t("loginButton")}
                </Button>
              ) : undefined
            }
          >
            {t(rateLimitError)}
          </Alert>
        )}

        {/* Share bar */}
        {hasResults && !loading && (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<WhatsApp />}
              onClick={handleShareAllWhatsApp}
              sx={{ color: "#25D366", borderColor: "#25D366", "&:hover": { borderColor: "#1ea952", backgroundColor: "rgba(37,211,102,0.04)" } }}
            >
              {t("shareAllWhatsApp")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Facebook />}
              onClick={handleShareFacebook}
              sx={{ color: "#1877F2", borderColor: "#1877F2", "&:hover": { borderColor: "#1565c0", backgroundColor: "rgba(24,119,242,0.04)" } }}
            >
              {t("shareFacebook")}
            </Button>
          </Box>
        )}

        {/* Results grid */}
        <Grid container spacing={3}>
          {loading
            ? Array(15).fill(0).map((_, i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                  <SkeletonCard />
                </Grid>
              ))
            : results.map((row, idx) => (
                <Grow in key={idx} timeout={300 + idx * 100}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <NameCard
                      name={row.name}
                      wordMeanings={row.wordMeanings}
                      meaning={row.meaning}
                      theme={theme}
                      feedbackState={feedbackMap[row.name] || null}
                      onFeedback={(isPositive) => handleFeedback(row.name, isPositive)}
                      onCopy={() => handleCopy(row.name)}
                      isFavorited={favoriteNames.has(row.name)}
                      favoritesDisabled={atFavoritesLimit}
                      onToggleFavorite={() => {
                        if (!user) { setLoginOpen(true); return; }
                        if (favoriteNames.has(row.name)) {
                          removeFavorite(row.name);
                        } else {
                          addFavorite(row.name, row.meaning, theme);
                          triggerOnboarding();
                        }
                      }}
                    />
                  </Grid>
                </Grow>
              ))}
        </Grid>

        {/* Re-discovery nudge — shown below results */}
        {hasResults && !loading && (
          <Box sx={{ mt: 5, mb: 2 }}>
            <DiscoveryPanel
              compact={false}
              title={t("rediscoveryNudge")}
              onSurprise={handleDiscoverySurprise}
              onVibe={handleDiscoveryVibe}
              onOrigin={handleDiscoveryOrigin}
              loading={loading}
            />
          </Box>
        )}

      </Container>

      {/* Beta footer */}
      <Box sx={{ textAlign: "center", py: 3, mt: 4 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
          {t("betaDisclaimer")}
        </Typography>
      </Box>

      <FavoritesDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {/* Onboarding popover */}
      <Popover
        open={onboardingPopoverOpen}
        anchorEl={bookmarkRef.current}
        onClose={() => setOnboardingPopoverOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        disableRestoreFocus
        PaperProps={{ sx: { p: 1.5, maxWidth: 200, borderRadius: 2, textAlign: "center" } }}
      >
        <Typography variant="caption">👆 {t("favoritesOnboardingTip")}</Typography>
      </Popover>

      {/* First-save snackbar */}
      <Snackbar
        open={firstSaveSnackbar}
        autoHideDuration={5000}
        onClose={() => setFirstSaveSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={t("favoritesFirstSaveSnackbar")}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => { setFirstSaveSnackbar(false); setDrawerOpen(true); }}
          >
            {t("favoritesSnackbarView")}
          </Button>
        }
      />

      {/* Favorites error snackbar */}
      <Snackbar
        open={!!favoritesError}
        autoHideDuration={3000}
        onClose={clearFavoritesError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={clearFavoritesError} severity="error" variant="filled" sx={{ width: "100%" }}>
          {t(favoritesError || "")}
        </Alert>
      </Snackbar>

      {/* Copy snackbar */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCopySnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {t("copiedSnackbar")}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default App;
